// ============================================
// useFollowAlerts.js  (follow-alerts-20261018)
// Alerts on followed wallets, set per wallet from the Wallets panel bell:
//   alerts.buys   "New token buys": the wallet buys a mint it has not
//                 bought, sold or held before (per-wallet seen list).
//   alerts.xfers  "Transfers out":  the wallet sends SOL or tokens to
//                 another wallet (not a swap).
//
// The server does the watching (server_swaps.py, watch_wallets action):
//   send: { action: 'watch_wallets', wallets: [{ address, buys, xfers }] }
//   recv: { type: 'follow_swap', wallet, side, mint, symbol, sol, mcap_usd, sig, ts }
//         { type: 'follow_transfer', wallet, to, mint, symbol, amount, sig, ts }
// The watch list is per socket, so it is re-sent whenever the socket
// (re)opens or the list changes.
//
// Each alert is a toast (same card as price alerts) + a row in the
// messages log. Toasts are rate-limited; the log always gets the row.
// ============================================
import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchWalletHoldings } from '../../utils/walletSources'
import { playBell } from './usePriceAlerts'

const SEEN_KEY = 'solcheddar_follow_seen'   // { [wallet]: { mints: [...], seeded: bool } }
const SEEN_CAP = 2000                       // mints remembered per wallet
const TOAST_MS = 12000                      // same lifetime as price-alert toasts
const TOAST_MAX_VISIBLE = 5
const TOAST_PER_MIN = 8                     // beyond this, log only
const BELL_GAP_MS = 4000

const short = (a) => (a ? `${a.slice(0, 4)}…${a.slice(-4)}` : '?')

function fmtUsd(v) {
  if (v == null || !Number.isFinite(v) || v <= 0) return null
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`
  return `$${v.toFixed(0)}`
}
function fmtAmt(v) {
  if (v == null || !Number.isFinite(v)) return '?'
  const a = Math.abs(v)
  if (a >= 1e9) return `${(v / 1e9).toFixed(2)}B`
  if (a >= 1e6) return `${(v / 1e6).toFixed(2)}M`
  if (a >= 1e4) return `${(v / 1e3).toFixed(1)}K`
  if (a >= 100) return v.toFixed(0)
  if (a >= 1) return v.toFixed(2)
  return v.toPrecision(2)
}

function loadSeen() {
  const m = new Map()
  try {
    const raw = JSON.parse(localStorage.getItem(SEEN_KEY) || '{}')
    for (const [w, v] of Object.entries(raw || {})) {
      if (!v || !Array.isArray(v.mints)) continue
      m.set(w, { mints: new Set(v.mints), seeded: !!v.seeded })
    }
  } catch {}
  return m
}

export default function useFollowAlerts({
  wsRef,            // main data WS ref (data.wsRef)
  status,           // data.status: flips on (re)connect, re-sends the watch list
  watchedWallets,   // [{ address, label, alerts: { buys, xfers } }]
  addMessage,       // App messages log: (text, kind, { mint, name })
}) {
  const [notifications, setNotifications] = useState([])
  const seenRef = useRef(null)
  if (seenRef.current === null) seenRef.current = loadSeen()
  const walletsRef = useRef(watchedWallets)
  walletsRef.current = watchedWallets
  const addMessageRef = useRef(addMessage)
  addMessageRef.current = addMessage
  const eventKeysRef = useRef(new Set())     // dedupe across reconnect replays
  const toastTimesRef = useRef([])
  const lastBellRef = useRef(0)
  const seedTriedRef = useRef(new Set())
  const saveTimerRef = useRef(null)

  const saveSeen = useCallback(() => {
    if (saveTimerRef.current) return
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null
      const out = {}
      // Only keep wallets still followed.
      const live = new Set((walletsRef.current || []).map(w => w.address))
      for (const [w, v] of seenRef.current) {
        if (!live.has(w)) continue
        let mints = [...v.mints]
        if (mints.length > SEEN_CAP) mints = mints.slice(-SEEN_CAP)
        out[w] = { mints, seeded: v.seeded }
      }
      try { localStorage.setItem(SEEN_KEY, JSON.stringify(out)) } catch {}
    }, 1500)
  }, [])

  const seenFor = (w) => {
    let v = seenRef.current.get(w)
    if (!v) { v = { mints: new Set(), seeded: false }; seenRef.current.set(w, v) }
    return v
  }

  // ===== Watch list -> server =====
  const watchList = (watchedWallets || [])
    .filter(w => w?.address && (w.alerts?.buys || w.alerts?.xfers))
    .map(w => ({ address: w.address, buys: !!w.alerts?.buys, xfers: !!w.alerts?.xfers }))
  const watchKey = JSON.stringify(watchList)
  const sentKeyRef = useRef(null)
  const sentWsRef = useRef(null)
  useEffect(() => {
    const ws = wsRef?.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    // Re-send on a new socket even if the list is unchanged.
    if (sentWsRef.current === ws && sentKeyRef.current === watchKey) return
    // Nothing armed and nothing ever sent on this socket: stay quiet.
    if (watchList.length === 0 && sentWsRef.current !== ws && sentKeyRef.current === null) return
    try {
      ws.send(JSON.stringify({ action: 'watch_wallets', wallets: watchList }))
      sentWsRef.current = ws
      sentKeyRef.current = watchKey
    } catch {}
  }, [wsRef?.current, status, watchKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // ===== Seed "already held" mints once per wallet (buys alerts only) =====
  // Without this, the first buy of a token the wallet already held before
  // being followed would read as "new". Best-effort: needs sign-in.
  useEffect(() => {
    for (const w of watchedWallets || []) {
      if (!w?.address || !w.alerts?.buys) continue
      const v = seenFor(w.address)
      if (v.seeded || seedTriedRef.current.has(w.address)) continue
      seedTriedRef.current.add(w.address)
      fetchWalletHoldings(w.address).then(h => {
        for (const t of h?.tokens || []) if (t?.mint) v.mints.add(t.mint)
        v.seeded = true
        saveSeen()
      }).catch(() => {})
    }
  }, [watchKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // ===== Toast + log =====
  const pushToast = useCallback((n) => {
    const now = Date.now()
    const times = toastTimesRef.current.filter(t => now - t < 60000)
    toastTimesRef.current = times
    if (times.length >= TOAST_PER_MIN) return          // log-only beyond the limit
    times.push(now)
    setNotifications(prev => {
      const next = [...prev, { ...n, ts: now }]
      return next.length > TOAST_MAX_VISIBLE ? next.slice(-TOAST_MAX_VISIBLE) : next
    })
    if (now - lastBellRef.current > BELL_GAP_MS) { lastBellRef.current = now; playBell() }
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden) {
        new Notification('SolCheddar Wallet Alert', { body: `${n.line} ${n.sub || ''}`.trim(), tag: n.id })
      }
    } catch {}
  }, [])

  useEffect(() => {
    const ws = wsRef?.current
    if (!ws) return
    const handler = (e) => {
      let msg
      try { msg = JSON.parse(e.data) } catch { return }
      if (msg?.type !== 'follow_swap' && msg?.type !== 'follow_transfer') return
      const w = (walletsRef.current || []).find(x => x.address === msg.wallet)
      if (!w) return
      const who = w.label || short(w.address)

      if (msg.type === 'follow_swap') {
        if (!msg.mint) return
        const v = seenFor(msg.wallet)
        const isNew = !v.mints.has(msg.mint)
        if (isNew) { v.mints.add(msg.mint); saveSeen() }
        if (msg.side !== 'buy' || !isNew || !w.alerts?.buys) return
        const key = `b:${msg.wallet}:${msg.mint}`
        if (eventKeysRef.current.has(key)) return
        eventKeysRef.current.add(key)
        const sym = msg.symbol || short(msg.mint)
        const mc = fmtUsd(msg.mcap_usd)
        const sol = Number.isFinite(msg.sol) ? `${fmtAmt(msg.sol)} SOL` : ''
        const detail = [sol, mc ? `mcap ${mc}` : null].filter(Boolean).join(' · ')
        addMessageRef.current?.(`👛 ${who} new buy${detail ? `: ${detail}` : ''}`, 'alert', { mint: msg.mint, name: msg.symbol || null })
        pushToast({
          id: `fw_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          kind: 'buy', title: 'Wallet New Buy', accent: 'green',
          line: `${who} bought ${sym}`, sub: detail ? `${detail} · click to view chart` : 'click to view chart',
          mint: msg.mint,
        })
        return
      }

      // follow_transfer
      if (!w.alerts?.xfers || !msg.to) return
      const key = `x:${msg.sig}:${msg.to}:${msg.mint || 'SOL'}`
      if (eventKeysRef.current.has(key)) return
      eventKeysRef.current.add(key)
      if (eventKeysRef.current.size > 2000) eventKeysRef.current.clear()
      const unit = msg.mint ? (msg.symbol || short(msg.mint)) : 'SOL'
      const amt = `${fmtAmt(Number(msg.amount))} ${unit}`
      const toFollowed = (walletsRef.current || []).find(x => x.address === msg.to)
      const toName = toFollowed ? (toFollowed.label || short(msg.to)) : short(msg.to)
      addMessageRef.current?.(`👛 ${who} sent ${amt} → ${toName}`, 'alert',
        msg.mint ? { mint: msg.mint, name: msg.symbol || null } : null)
      pushToast({
        id: `fw_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        kind: 'xfer', title: 'Wallet Transfer', accent: 'amber',
        line: `${who} sent ${amt} → ${toName}`, sub: 'click for wallet map',
        to: msg.to, canFollow: !toFollowed,
      })
    }
    ws.addEventListener('message', handler)
    return () => ws.removeEventListener('message', handler)
  }, [wsRef?.current, status, pushToast, saveSeen]) // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }, [])

  // Auto-dismiss, same lifetime as price-alert toasts.
  useEffect(() => {
    if (notifications.length === 0) return
    const timers = notifications.map(n =>
      setTimeout(() => dismiss(n.id), Math.max(0, TOAST_MS - (Date.now() - n.ts))))
    return () => timers.forEach(clearTimeout)
  }, [notifications, dismiss])

  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current) }, [])

  return { notifications, dismiss }
}
